<template>
  <div
    ref="scroller"
    class="conversation gap-4 h-full overflow-y-auto pr-1 mb-4"
    data-testid="ai-chat-conversation-scroller"
    @scroll="onScroll"
  >
    <div ref="content" class="flex flex-col gap-4">
      <chat-turn
        v-for="turn in turns"
        :key="turn.localId"
        :turn="turn"
        :disabled="isStreaming"
        @followup="emit('followup', $event)"
        @resend="emit('resend', $event)"
        @download="emit('download', $event)"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import ChatTurn from './chat-turn.vue'
import type { CardBlock, Turn } from '../types'

/* how close to the bottom still counts as following the answer */
const PIN_THRESHOLD_PX = 40

const props = withDefaults(
  defineProps<{
    /* turns in chat order, see utils/turn.ts */
    turns?: Turn[]
  }>(),
  { turns: () => [] }
)

const emit = defineEmits<{
  followup: [prompt: string]
  resend: [turn: Turn]
  download: [block: CardBlock]
}>()

const scroller = ref<HTMLElement | null>(null)
const content = ref<HTMLElement | null>(null)

const isStreaming = computed(() => props.turns.at(-1)?.status === 'streaming')

/* false once the merchant scrolls up to read; scrolling back near the bottom follows the answer again */
let isPinned = true
let observer: ResizeObserver | undefined

const scrollToBottom = (): void => {
  if (scroller.value) scroller.value.scrollTop = scroller.value.scrollHeight
}

const onScroll = (): void => {
  const element = scroller.value
  if (!element) return
  const distance =
    element.scrollHeight - element.scrollTop - element.clientHeight
  isPinned = distance <= PIN_THRESHOLD_PX
}

/* the markdown renders asynchronously, so the content's size, not the turn data, says when the answer grew */
onMounted(() => {
  scrollToBottom()
  if (!content.value || typeof ResizeObserver === 'undefined') return
  observer = new ResizeObserver(() => {
    if (isPinned) scrollToBottom()
  })
  observer.observe(content.value)
})

onBeforeUnmount(() => {
  observer?.disconnect()
})
</script>

<style scoped>
.conversation {
  display: flex;
  flex-direction: column;
}
</style>
